import { useCallback, useEffect, useRef, useState } from "react";

// EK-442 — Speech-to-Text via Web Speech API (SpeechRecognition).
// pt-BR, resultados intermediários, auto-envio após silêncio e threshold de
// confiança para evitar disparos por ruído de fundo.

const SpeechRecognitionImpl: SpeechRecognitionConstructor | undefined =
	typeof window !== "undefined" ? window.SpeechRecognition ?? window.webkitSpeechRecognition : undefined;

export const isSTTSupported = (): boolean => !!SpeechRecognitionImpl;

export interface UseSTTOptions {
	lang?: string;
	/** Tempo de silêncio (ms) após o qual a fala final é confirmada. */
	silenceMs?: number;
	/** Confiança mínima (0–1) para aceitar um trecho final. */
	confidenceThreshold?: number;
	/** Disparado quando uma fala final passa pelo threshold após o silêncio. */
	onFinalTranscript?: (transcript: string) => void;
	/** Disparado em erros não-fatais (ex.: permissão negada). */
	onError?: (error: SpeechRecognitionErrorCode) => void;
}

export interface UseSTTReturn {
	supported: boolean;
	listening: boolean;
	/** Transcrição parcial (interim) acumulada + final ainda não confirmada. */
	interimTranscript: string;
	/** Última permissão de microfone negada? */
	permissionDenied: boolean;
	start: () => void;
	stop: () => void;
	abort: () => void;
	/** Limpa a transcrição parcial exibida. */
	clear: () => void;
}

export function useSTT(options: UseSTTOptions = {}): UseSTTReturn {
	const { lang = "pt-BR", silenceMs = 1500, confidenceThreshold = 0.5, onFinalTranscript, onError } = options;

	const [listening, setListening] = useState(false);
	const [interimTranscript, setInterimTranscript] = useState("");
	const [permissionDenied, setPermissionDenied] = useState(false);

	const recognitionRef = useRef<SpeechRecognition | null>(null);
	const silenceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
	// Buffer da fala final acumulada na sessão atual de escuta.
	const finalBufferRef = useRef("");
	const bestConfidenceRef = useRef(0);
	// Mantém callbacks atualizados sem recriar o reconhecimento.
	const onFinalRef = useRef(onFinalTranscript);
	const onErrorRef = useRef(onError);
	// Flag para reiniciar automaticamente quando o navegador encerra a sessão
	// (Chrome encerra após ~60s ou em silêncio prolongado) enquanto ainda ouvimos.
	const wantListeningRef = useRef(false);

	useEffect(() => {
		onFinalRef.current = onFinalTranscript;
		onErrorRef.current = onError;
	}, [onFinalTranscript, onError]);

	const clearSilenceTimer = useCallback(() => {
		if (silenceTimerRef.current) {
			clearTimeout(silenceTimerRef.current);
			silenceTimerRef.current = null;
		}
	}, []);

	const flushFinal = useCallback(() => {
		const text = finalBufferRef.current.trim();
		const confident = bestConfidenceRef.current >= confidenceThreshold;
		finalBufferRef.current = "";
		bestConfidenceRef.current = 0;
		setInterimTranscript("");
		if (text && confident) {
			onFinalRef.current?.(text);
		}
	}, [confidenceThreshold]);

	const ensureRecognition = useCallback((): SpeechRecognition | null => {
		if (!SpeechRecognitionImpl) return null;
		if (recognitionRef.current) return recognitionRef.current;

		const recognition = new SpeechRecognitionImpl();
		recognition.lang = lang;
		recognition.continuous = true;
		recognition.interimResults = true;
		recognition.maxAlternatives = 1;

		recognition.onresult = (event: SpeechRecognitionEvent) => {
			let interim = "";
			for (let i = event.resultIndex; i < event.results.length; i++) {
				const result = event.results[i];
				const alt = result[0];
				if (result.isFinal) {
					finalBufferRef.current += (finalBufferRef.current ? " " : "") + alt.transcript.trim();
					// confidence pode vir 0 em alguns engines; nesse caso assume aceitável.
					bestConfidenceRef.current = Math.max(bestConfidenceRef.current, alt.confidence || 1);
				} else {
					interim += alt.transcript;
				}
			}
			setInterimTranscript((finalBufferRef.current + " " + interim).trim());

			// Reinicia o timer de silêncio a cada token recebido.
			clearSilenceTimer();
			silenceTimerRef.current = setTimeout(flushFinal, silenceMs);
		};

		recognition.onerror = (event: SpeechRecognitionErrorEvent) => {
			if (event.error === "not-allowed" || event.error === "service-not-allowed") {
				setPermissionDenied(true);
				wantListeningRef.current = false;
			}
			// "no-speech"/"aborted" são transitórios; não propaga como falha dura.
			if (event.error !== "no-speech" && event.error !== "aborted") {
				onErrorRef.current?.(event.error);
			}
		};

		recognition.onend = () => {
			// Reinicia se o usuário ainda quer ouvir (sessão encerrada pelo browser).
			if (wantListeningRef.current) {
				try {
					recognition.start();
					return;
				} catch {
					/* start em corrida — ignora */
				}
			}
			setListening(false);
		};

		recognitionRef.current = recognition;
		return recognition;
	}, [lang, silenceMs, clearSilenceTimer, flushFinal]);

	const start = useCallback(() => {
		const recognition = ensureRecognition();
		if (!recognition) return;
		finalBufferRef.current = "";
		bestConfidenceRef.current = 0;
		setInterimTranscript("");
		setPermissionDenied(false);
		wantListeningRef.current = true;
		try {
			recognition.start();
			setListening(true);
		} catch {
			// start() lança se já estiver ativo — estado já reflete escuta.
			setListening(true);
		}
	}, [ensureRecognition]);

	const stop = useCallback(() => {
		wantListeningRef.current = false;
		clearSilenceTimer();
		// Confirma o que houver em buffer antes de parar.
		flushFinal();
		recognitionRef.current?.stop();
		setListening(false);
	}, [clearSilenceTimer, flushFinal]);

	const abort = useCallback(() => {
		wantListeningRef.current = false;
		clearSilenceTimer();
		finalBufferRef.current = "";
		bestConfidenceRef.current = 0;
		setInterimTranscript("");
		recognitionRef.current?.abort();
		setListening(false);
	}, [clearSilenceTimer]);

	const clear = useCallback(() => {
		finalBufferRef.current = "";
		bestConfidenceRef.current = 0;
		setInterimTranscript("");
	}, []);

	// Cleanup ao desmontar.
	useEffect(() => {
		return () => {
			wantListeningRef.current = false;
			clearSilenceTimer();
			recognitionRef.current?.abort();
			recognitionRef.current = null;
		};
	}, [clearSilenceTimer]);

	return {
		supported: !!SpeechRecognitionImpl,
		listening,
		interimTranscript,
		permissionDenied,
		start,
		stop,
		abort,
		clear,
	};
}
